/**
 * Engine arena geometry — rectangular play bounds, wall-aware helpers.
 * Domain owns what the bounds mean (map tables, spawn pads); this kit only
 * does rectangle math so AI goals/velocities can respect walls at PLAN time
 * instead of relying on post-clamp (which causes wall-pressing jitter).
 */

export type Rect2D = {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
};

/**
 * Clamp a point into the rect shrunk by `inset`. Center-anchored so an
 * oversized inset (boss radius > half span) never inverts the rect.
 */
export function clampPointToRect(
  px: number,
  py: number,
  r: Rect2D,
  inset = 0,
): { x: number; y: number } {
  const cx = (r.xMin + r.xMax) / 2;
  const cy = (r.yMin + r.yMax) / 2;
  const hx = Math.max(0, (r.xMax - r.xMin) / 2 - inset);
  const hy = Math.max(0, (r.yMax - r.yMin) / 2 - inset);
  return {
    x: Math.min(cx + hx, Math.max(cx - hx, px)),
    y: Math.min(cy + hy, Math.max(cy - hy, py)),
  };
}

/** In-place variant for hot paths (scratch buffer convention). */
export function clampGoalToRect(
  gx: number,
  gy: number,
  r: Rect2D,
  inset: number,
  out: { x: number; y: number },
): void {
  const p = clampPointToRect(gx, gy, r, inset);
  out.x = p.x;
  out.y = p.y;
}

export function insetRect(r: Rect2D, inset: number): Rect2D {
  const cx = (r.xMin + r.xMax) / 2;
  const cy = (r.yMin + r.yMax) / 2;
  const hx = Math.max(0, (r.xMax - r.xMin) / 2 - inset);
  const hy = Math.max(0, (r.yMax - r.yMin) / 2 - inset);
  return { xMin: cx - hx, xMax: cx + hx, yMin: cy - hy, yMax: cy + hy };
}

/** Distance from a point to the nearest rect edge (negative when outside). */
export function distToNearestEdge(x: number, y: number, r: Rect2D): number {
  return Math.min(x - r.xMin, r.xMax - x, y - r.yMin, r.yMax - y);
}

/**
 * Kill velocity components that push a body already at/over an edge further
 * out (prevents per-frame wall pressing after position clamp).
 */
export function zeroOutwardVelocity(
  e: { x: number; y: number; vx: number; vy: number },
  r: Rect2D,
  eps = 0.75,
): void {
  if (e.x <= r.xMin + eps && e.vx < 0) e.vx = 0;
  if (e.x >= r.xMax - eps && e.vx > 0) e.vx = 0;
  if (e.y <= r.yMin + eps && e.vy < 0) e.vy = 0;
  if (e.y >= r.yMax - eps && e.vy > 0) e.vy = 0;
}

/**
 * Strip the wall-facing component from a separation impulse for a body near
 * an edge — soft-sep must never grind the crowd into the wall.
 */
export function wallAwareSepImpulse(
  x: number,
  y: number,
  sx: number,
  sy: number,
  r: Rect2D,
  edgeMargin = 2,
): { x: number; y: number } {
  let ox = sx;
  let oy = sy;
  if (x <= r.xMin + edgeMargin && ox < 0) ox = 0;
  if (x >= r.xMax - edgeMargin && ox > 0) ox = 0;
  if (y <= r.yMin + edgeMargin && oy < 0) oy = 0;
  if (y >= r.yMax - edgeMargin && oy > 0) oy = 0;
  return { x: ox, y: oy };
}

/**
 * True when a retreat direction from (x, y) points into a nearby edge —
 * callers should veto or re-compose the retreat instead of pressing the wall.
 * `dirEps` ignores near-perpendicular components (e.g. 0.15 for normalized dirs).
 */
export function retreatBlockedAtEdge(
  x: number,
  y: number,
  dirX: number,
  dirY: number,
  r: Rect2D,
  margin = 14,
  dirEps = 0,
): boolean {
  if (dirX < -dirEps && x <= r.xMin + margin) return true;
  if (dirX > dirEps && x >= r.xMax - margin) return true;
  if (dirY < -dirEps && y <= r.yMin + margin) return true;
  if (dirY > dirEps && y >= r.yMax - margin) return true;
  return false;
}
